<script setup lang="ts">
import { onErrorCaptured, ref } from "vue";
import { isNavigationFailure, NavigationFailureType, useRouter } from "vue-router";

import { useRuntimeErrorStore } from "@/stores/runtimeError";

const runtimeErrors = useRuntimeErrorStore();
const router = useRouter();
const renderKey = ref(0);

onErrorCaptured((caught) => {
  runtimeErrors.capture("render", caught, ["retry-view", "go-home", "reload"]);
  return false;
});

function retryView(): void {
  runtimeErrors.clear();
  renderKey.value += 1;
}

async function goHome(): Promise<void> {
  try {
    const result = await router.push("/");
    if (
      isNavigationFailure(
        result,
        NavigationFailureType.aborted | NavigationFailureType.cancelled,
      )
    ) {
      return;
    }
    runtimeErrors.clear();
  } catch {
    // Keep the recovery surface available when navigation cannot complete.
  }
}

function reload(): void {
  window.location.reload();
}
</script>

<template>
  <section
    v-if="runtimeErrors.failure"
    role="alert"
  >
    <h1>{{ runtimeErrors.failure.title }}</h1>
    <p>{{ runtimeErrors.failure.message }}</p>
    <div>
      <button
        v-if="runtimeErrors.failure.actions.includes('retry-view')"
        type="button"
        @click="retryView"
      >
        Retry
      </button>
      <button
        v-if="runtimeErrors.failure.actions.includes('go-home')"
        type="button"
        @click="goHome"
      >
        Go home
      </button>
      <button
        v-if="runtimeErrors.failure.actions.includes('reload')"
        type="button"
        @click="reload"
      >
        Reload
      </button>
    </div>
  </section>
  <div
    v-else
    :key="renderKey"
    :data-view-attempt="renderKey"
  >
    <slot />
  </div>
</template>
